import { Application } from 'app/application';
import { LoggerService } from 'app/logger/logger.service';
import { ModelService } from 'app/model/services/model.service';
import { FactoryUrlInformationService } from 'app/services/factory-url-information.service';
import { ArmTemplateGeneratorService } from 'app/model/services/arm-template-generator.service';
import { convertObjectToString, format } from 'app/shared/utils/string.util';
import { getCmdApiInjector } from 'cmd-api/cmd-api.injector.definition';
import { ValidatorUtils } from 'app/model/base/validator.utils';
import { IOwnedViolation, IResourceContext, GlobalVariables as ShellGlobalVariables } from 'shell-models-lib';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { GlobalVariables } from 'app/services/globals';
import { PublisherUtils } from 'app/model/services/publisher/publisher.utils';

import { PublishConfigService } from 'app/model/services/publisher/publish-config.service';
import { ISynapsePool, LocalFileUtils } from 'cmd-api/utils/local-file.utils';
import { AssetUtils } from 'cmd-api/utils/assets.utils';
import { IARMTemplateOptions } from './local-resource-provider.tokens';
import { initializeMetadata } from 'app/shared/utils/connector-metadata-initialization.util';
import { RootApplication } from 'app/multi-contexts/modules/root/root.application';
import { EnvironmentInjector, Injector } from '@angular/core';
import { RESOURCE_CONTEXT_DATA, RESOURCE_CONTEXT_DATA_TOKEN } from 'app/multi-contexts/resource-context.typings';

export class CmdApiApp {
    public static resourcesLoaded: Observable<boolean> = new Observable();
    private static readonly className = 'CmdApiApp';
    private static _resourcesLoaded: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private static _templateName = __IS_A365__ ? stringResources.A365Template : stringResources.ARMTemplate;
    private static _rootFolder: string = '';
    private static _injector: Injector;

    private static get loggerService(): LoggerService {
        return this._injector.get<LoggerService>(LoggerService);
    }

    public static async initialize(
        rootFolder: string,
        factoryId: string,
        armTemplateOptions?: IARMTemplateOptions,
        featureFlags?: string
    ): Promise<void> {
        this._rootFolder = rootFolder;
        this.resourcesLoaded = this._resourcesLoaded;

        this._injector = getCmdApiInjector(rootFolder, armTemplateOptions, factoryId);
        RootApplication.initialize(this._injector);
        Application.initialize(this._injector);
        this.loggerService.log(
            this.className,
            `Initializing application.\nResource: ${factoryId}\nRootFolder: ${rootFolder}`
        );
        const modelService = this._injector.get<ModelService>(ModelService);
        const urlService = this._injector.get<FactoryUrlInformationService>(FactoryUrlInformationService);
        const publishConfigService = this._injector.get<PublishConfigService>(PublishConfigService);

        CmdApiApp.setFeatureFlagsExplicitly(featureFlags);

        urlService.setFactoryInfo({
            factory: factoryId
        });
        if (__IS_A365__) {
            // Need to push synapse pool resources as dummy files to local folder as they are not synced to git.
            // So the model service can retain the pool references in the pipeline artifacts
            const synapsePools: ISynapsePool[] = await LocalFileUtils.generateSynapsePoolFiles(
                this.loggerService,
                rootFolder
            );
            synapsePools.forEach((synapsePool) =>
                LocalFileUtils.writeFiles(this.loggerService, synapsePool.poolFiles, synapsePool.poolType, rootFolder)
            );
        }
        this.loggerService.log(this.className, 'Initializing resource registries...');
        await initializeMetadata(this._injector.get(EnvironmentInjector));
        this.loggerService.log(this.className, 'Initializing model service...');
        await modelService.initialize();
        this.loggerService.log(this.className, 'Initializing publish config service...');
        await publishConfigService.initialize();
        this.loggerService.log(this.className, 'App initialized.');
        modelService.isFetching
            .pipe(distinctUntilChanged())
            .subscribe(async (fetchProgress: boolean | number | string) => {
                if (fetchProgress === 0) {
                    this._resourcesLoaded.next(true);
                }
            });
    }

    public static async exportArmTemplate(outputFolder?: string, usePreviewTools?: boolean): Promise<void> {
        try {
            const resources = await PublisherUtils.getPublishableNameds(
                this._injector.get<IResourceContext>(RESOURCE_CONTEXT_DATA_TOKEN)
            );
            this.loggerService.log(this.className, `Publishable resource count: ${resources.length}`);
            const publishConfigService = this._injector.get<PublishConfigService>(PublishConfigService);
            const modelService = this._injector.get<ModelService>(ModelService);

            if (resources.length === 0) {
                // we didn't find any resouces, now need to inspect the global parameters for exporting further
                const gParams = await PublisherUtils.createGlobalParametersForArmTemplate(
                    this._injector.get<IResourceContext>(RESOURCE_CONTEXT_DATA_TOKEN)
                );
                this.loggerService.log(this.className, `Publishable parameters count: ${gParams.parameters.size}`);

                await publishConfigService.initialize();

                if (
                    !publishConfigService.publishConfig ||
                    !publishConfigService.publishConfig.includeGlobalParamsTemplate ||
                    gParams.parameters.size === 0
                ) {
                    // We have nothing to publish now, throw error and cancel template exporting
                    throw new Error(
                        format(
                            stringResources.CmdApiExportResourceNotFoundError,
                            this._rootFolder,
                            CmdApiApp._templateName
                        )
                    );
                }
            }

            const violations = await this.validateResources();
            if (violations.length) {
                throw new Error(format(stringResources.CmdApiExportTemplateValidationError, CmdApiApp._templateName));
            }

            const armTemplateGenerator = this._injector.get<ArmTemplateGeneratorService>(ArmTemplateGeneratorService);
            const armTemplates = await armTemplateGenerator.getAllArmTemplates(null);
            LocalFileUtils.writeFiles(this.loggerService, armTemplates, outputFolder);
            this.loggerService.log(this.className, `${CmdApiApp._templateName} generated. Writing files...`);
            if (!__IS_A365__) {
                LocalFileUtils.writeFiles(
                    this.loggerService,
                    await AssetUtils.getDeploymentHelperFiles(
                        this._injector.get<IResourceContext>(RESOURCE_CONTEXT_DATA_TOKEN),
                        usePreviewTools,
                        { modelService, publishConfigService }
                    ),
                    outputFolder
                );
            }
            this.loggerService.log(this.className, `${CmdApiApp._templateName} export finished successfully.`);
        } catch (error) {
            this.loggerService.error(
                this.className,
                `Failed to export ${CmdApiApp._templateName}. Error: ${convertObjectToString(error)}`
            );
            throw error;
        }
    }

    public static async validateResources(): Promise<IOwnedViolation[]> {
        let violations: IOwnedViolation[] = [];
        try {
            this.loggerService.log(this.className, `Starting to validate all resources`);
            violations = await ValidatorUtils.validateAllModels(this._injector.get<ModelService>(ModelService), {
                contextData: this._injector.get(RESOURCE_CONTEXT_DATA)
            });
            if (violations.length) {
                const validationErrorMessage = __IS_A365__
                    ? stringResources.CmdApiValidationErrorA365
                    : stringResources.CmdApiValidationError;
                this.loggerService.error(
                    this.className,
                    `\n${format(validationErrorMessage, `${violations.length}`)}\n`
                );
                violations.forEach((violation) => {
                    const resourceName =
                        (violation.owned &&
                            violation.owned.rootOwner &&
                            violation.owned.rootOwner.named &&
                            violation.owned.rootOwner.named.name) ||
                        '';
                    this.loggerService.error(
                        this.className,
                        `Resource '${resourceName}' has the following validation error: ${violation.message()}`
                    );
                });
                const urlService = this._injector.get<FactoryUrlInformationService>(FactoryUrlInformationService);
                const url = __IS_A365__
                    ? 'https://web.azuresynapse.net'
                    : `https://adf.azure.com/authoring?factory=/${urlService.getResourceId()}`;
                this.loggerService.error(this.className, `\n\nGo to:\n${url}\nto fix the validation errors.\n\n`);
            } else {
                this.loggerService.log(this.className, `\nValidation finished. No errors found.`);
            }
        } catch (error) {
            this.loggerService.error(
                this.className,
                `Failed to run resource validation. Error: ${convertObjectToString(error)}`
            );
            throw error;
        }
        return violations;
    }

    private static setFeatureFlagsExplicitly(featureflags?: string) {
        // To indicate this is NPM Utility
        GlobalVariables.isCmdApiApp = true;
        ShellGlobalVariables.isCmdApiApp = true;

        // These feature are enabled for public but the flags are not cleaned-up yet,
        // so we need to explicitly set them in NPM utility to avoid arm-template inconsistencies.
        GlobalVariables.enableSsisIrVNetV2FeatureWithFF = true;
        GlobalVariables.enableMicrosoft365DataFlowWithFF = true;
        GlobalVariables.supportPreserveDatetime = true; // enable it by default in CMD to avoid value loss
        GlobalVariables.enableOracleDataFlowWithFF = true;
        GlobalVariables.enableGoogleCloudStorageSourceDataFlowWithFF = true;
        GlobalVariables.enableLargeActivityLimit = true;
        ShellGlobalVariables.maxActivityLimit = 120;
        GlobalVariables._enableGen1MIAuthForMDF = true;

        if (featureflags) {
            const flags = featureflags.split(',');
            for (const ind in flags) {
                const flag = flags[ind].split('=');
                this.loggerService.log(this.className, `setting this flags: ${flag[0]} and value:${flag[1]}`);
                // set the global variable based on the flag here, but it is not dynamic
            }
        }
    }
}
